import React, { useMemo } from 'react';
import type {
  ColDef,
  EditableCallbackParams,
  ValueGetterParams,
  ValueSetterParams,
} from 'ag-grid-community';
import { LopCrudPage } from '../../components/LopCrudPage';
import {
  getCalcMethodMap,
  saveCalcMethodMapBatch,
} from '../../api/calcMethodMap';
import type { CalcMethodMap } from '../../types/CalcMethodMap';
import { useBalanceTypes } from '../../hooks/useBalanceTypes';
import { useAssetTypes } from '../../hooks/useAssetTypes';
import { useCalcMethods } from '../../hooks/useCalcMethods';
import { t } from '../../i18n/t';

// All 4 columns are FKs to lookup tables; each renders as an `agSelectCellEditor`
// dropdown. Labels:
//   - BalanceTypes / AssetTypes: "<id> - <name>"  (no English code on these tables)
//   - CalcMethods (filtered by calcTypeId): "<calcMethodCode> - <calcMethodName>"
// Nullable columns (calcMethodId, exposureCalcMethodId) include a "(ללא)" option.
// Orphan ids (not in the loaded lookup set) fall back to the raw id so dirty data
// stays visible/editable — same as the AssetsPage dropdown contract.
//
// Per the AgGrid dropdown rule (CLAUDE.md "cellDataType inference breaks custom
// value mapping"), every dropdown column carries cellDataType:'text' even though
// the underlying field is a number — without it, AgGrid coerces the picked label
// back to NaN before the valueSetter runs.

const NONE_LABEL = '(ללא)';

const editableOnAdd = (params: EditableCallbackParams<CalcMethodMap>) =>
  (params.data as { __status?: string } | undefined)?.__status === 'added';

export function CalcMethodMapPage() {
  const balanceTypes = useBalanceTypes();
  const assetTypes = useAssetTypes();
  const calcMethods = useCalcMethods();

  const columnDefs = useMemo<ColDef<CalcMethodMap>[]>(() => {
    // BalanceTypes: "<id> - <name>"
    const balLabelById = new Map<number, string>();
    const balIdByLabel = new Map<string, number>();
    (balanceTypes.data ?? []).forEach((b) => {
      const label = `${b.balanceTypeId} - ${b.balanceTypeName}`;
      balLabelById.set(b.balanceTypeId, label);
      balIdByLabel.set(label, b.balanceTypeId);
    });
    const balLabels = Array.from(balIdByLabel.keys());

    // AssetTypes: "<id> - <name>"
    const astLabelById = new Map<number, string>();
    const astIdByLabel = new Map<string, number>();
    (assetTypes.data ?? []).forEach((a) => {
      const label = `${a.assetTypeId} - ${a.assetTypeName}`;
      astLabelById.set(a.assetTypeId, label);
      astIdByLabel.set(label, a.assetTypeId);
    });
    const astLabels = Array.from(astIdByLabel.keys());

    // CalcMethods — split by calcTypeId (1=exposure, 2=valuation). Use the English
    // mnemonic (calcMethodCode) as the visible code; names can be long.
    const valLabelById = new Map<number, string>();
    const valIdByLabel = new Map<string, number>();
    const expLabelById = new Map<number, string>();
    const expIdByLabel = new Map<string, number>();
    (calcMethods.data ?? []).forEach((m) => {
      const label = `${m.calcMethodCode} - ${m.calcMethodName}`;
      if (m.calcTypeId === 2) {
        valLabelById.set(m.calcMethodId, label);
        valIdByLabel.set(label, m.calcMethodId);
      } else if (m.calcTypeId === 1) {
        expLabelById.set(m.calcMethodId, label);
        expIdByLabel.set(label, m.calcMethodId);
      }
    });
    const valLabels = [NONE_LABEL, ...Array.from(valLabelById.values())];
    const expLabels = [NONE_LABEL, ...Array.from(expLabelById.values())];

    return [
      {
        field: 'balanceTypeId',
        headerName: t('calcMethodMap.columns.balanceTypeId'),
        width: 220,
        cellDataType: 'text',
        editable: editableOnAdd,
        sort: 'asc',
        valueGetter: (p: ValueGetterParams<CalcMethodMap>) => {
          const id = p.data?.balanceTypeId;
          if (id == null) return '';
          return balLabelById.get(id) ?? String(id);
        },
        valueSetter: (p: ValueSetterParams<CalcMethodMap>) => {
          const id = balIdByLabel.get(p.newValue as string);
          if (id == null) return false;
          (p.data as CalcMethodMap).balanceTypeId = id;
          return true;
        },
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: { values: balLabels },
      },
      {
        field: 'assetTypeId',
        headerName: t('calcMethodMap.columns.assetTypeId'),
        width: 260,
        cellDataType: 'text',
        editable: editableOnAdd,
        valueGetter: (p: ValueGetterParams<CalcMethodMap>) => {
          const id = p.data?.assetTypeId;
          if (id == null) return '';
          return astLabelById.get(id) ?? String(id);
        },
        valueSetter: (p: ValueSetterParams<CalcMethodMap>) => {
          const id = astIdByLabel.get(p.newValue as string);
          if (id == null) return false;
          (p.data as CalcMethodMap).assetTypeId = id;
          return true;
        },
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: { values: astLabels },
      },
      {
        field: 'calcMethodId',
        headerName: t('calcMethodMap.columns.calcMethodId'),
        width: 260,
        cellDataType: 'text',
        editable: true,
        valueGetter: (p: ValueGetterParams<CalcMethodMap>) => {
          const id = p.data?.calcMethodId;
          if (id == null) return NONE_LABEL;
          return valLabelById.get(id) ?? String(id);
        },
        valueSetter: (p: ValueSetterParams<CalcMethodMap>) => {
          const label = p.newValue as string | null;
          if (label == null || label === '' || label === NONE_LABEL) {
            (p.data as CalcMethodMap).calcMethodId = null;
            return true;
          }
          const id = valIdByLabel.get(label);
          if (id == null) return false;
          (p.data as CalcMethodMap).calcMethodId = id;
          return true;
        },
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: { values: valLabels },
      },
      {
        field: 'exposureCalcMethodId',
        headerName: t('calcMethodMap.columns.exposureCalcMethodId'),
        flex: 1,
        cellDataType: 'text',
        editable: true,
        valueGetter: (p: ValueGetterParams<CalcMethodMap>) => {
          const id = p.data?.exposureCalcMethodId;
          if (id == null) return NONE_LABEL;
          return expLabelById.get(id) ?? String(id);
        },
        valueSetter: (p: ValueSetterParams<CalcMethodMap>) => {
          const label = p.newValue as string | null;
          if (label == null || label === '' || label === NONE_LABEL) {
            (p.data as CalcMethodMap).exposureCalcMethodId = null;
            return true;
          }
          const id = expIdByLabel.get(label);
          if (id == null) return false;
          (p.data as CalcMethodMap).exposureCalcMethodId = id;
          return true;
        },
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: { values: expLabels },
      },
    ];
  }, [balanceTypes.data, assetTypes.data, calcMethods.data]);

  const newCalcMethodMapDefaults = (): Partial<CalcMethodMap> => ({
    balanceTypeId: 0,
    assetTypeId: 0,
    calcMethodId: null,
    exposureCalcMethodId: null,
  });

  return (
    <LopCrudPage<CalcMethodMap>
      resourceCode="calcMethodMap"
      titleKey="calcMethodMap.pageTitle"
      idField="idKey"
      getRows={getCalcMethodMap}
      saveBatch={(added, modified, deletedIds) =>
        saveCalcMethodMapBatch(added, modified, deletedIds as string[])
      }
      columnDefs={columnDefs}
      newRowDefaults={newCalcMethodMapDefaults}
    />
  );
}
